import React from 'react';
import { FaIdBadge, FaRegCalendar, FaRegClock, FaInfo } from 'react-icons/fa';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { ILeave } from 'routes/client/leaves/types';
import { formatDate } from 'utils/helpers/formatters';
import { useModal } from 'store/modal';

interface Props {
  leaveId: number;
  leaveDetails: ILeave;
  marginLeft?: boolean;
}

const LeaveInterruptionInfoButton: React.FC<Props> = ({ leaveId, leaveDetails, marginLeft }) => {
  const { t } = useTranslation();
  const { showModal } = useModal();

  const { interrupting_sick_leave: interruptingSickLeave, interrupted_leaves: interruptedLeaves } = leaveDetails;

  if (!interruptingSickLeave && (!interruptedLeaves || interruptedLeaves.length === 0)) return null;

  return (
    <div className={`d-inline-block ${marginLeft ? '' : 'ml-2'}`}>
      <TooltipWrapper
        id={`tooltip-interruption-info-${leaveId}`}
        overlay={t('leaves.interruptionInfo')}
      >
        <Button
          type="button"
          btnType="secondary"
          size="sm"
          onClick={() => {
            showModal({
              type: 'info',
              size: 'lg',
              content: (
                <div>
                  {interruptingSickLeave && (
                    <div>
                      <h2 className="mb-4">{t('leaves.interruptedBy')}</h2>
                      <LeaveDetails leave={interruptingSickLeave} />
                    </div>
                  )}
                  {interruptedLeaves && interruptedLeaves.length > 0 && (
                    <div>
                      <h2 className="mb-4">{t('leaves.interrupts')}</h2>
                      {interruptedLeaves.map((leave: ILeave) => (
                        <LeaveDetails
                          key={leave.id}
                          leave={leave}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ),
            });
          }}
        >
          <FaInfo size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  );
};

export default LeaveInterruptionInfoButton;

const LeaveDetails = ({ leave }: { leave: ILeave }) => {
  const { t } = useTranslation();

  return (
    <LeaveContainer>
      <IconTextContainer>
        <IconBackground color="#2b90ef">
          <FaIdBadge
            size={16}
            color="#2b90ef"
          />
        </IconBackground>
        <TextContainer>
          <LeaveInfoText>{t('leaves.id')}</LeaveInfoText>
          <YearInfoText>{leave.id}</YearInfoText>
        </TextContainer>
      </IconTextContainer>
      <IconTextContainer>
        <IconBackground color="#ffc107">
          <FaRegClock
            size={16}
            color="#ffc107"
          />
        </IconBackground>
        <TextContainer>
          <LeaveInfoText>{t('leaves.duration')}</LeaveInfoText>
          <YearInfoText>
            {leave.leave_days} {t('leaves.days')}
          </YearInfoText>
        </TextContainer>
      </IconTextContainer>
      <IconTextContainer>
        <IconBackground color="#28a745">
          <FaRegCalendar
            size={16}
            color="#28a745"
          />
        </IconBackground>
        <TextContainer>
          <LeaveInfoText>{t('leaves.fromInterrupted')}</LeaveInfoText>
          <YearInfoText>
            {formatDate(new Date(leave.date_from), 'dd.mm.YYY')} {t('leaves.yearShortOnlyBG')}
          </YearInfoText>
        </TextContainer>
      </IconTextContainer>
      <IconTextContainer>
        <IconBackground color="#ef476f">
          <FaRegCalendar
            size={16}
            color="#ef476f"
          />
        </IconBackground>
        <TextContainer>
          <LeaveInfoText>{t('leaves.toInterrupted')}</LeaveInfoText>
          <YearInfoText>
            {formatDate(new Date(leave.date_to), 'dd.mm.YYY')} {t('leaves.yearShortOnlyBG')}
          </YearInfoText>
        </TextContainer>
      </IconTextContainer>
    </LeaveContainer>
  );
};

const LeaveContainer = styled.div`
  margin-bottom: 1rem;
  padding: 1rem;
  border: 1px solid var(--light);
  background-color: #f8f9fa;
  border-radius: 15px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 80px;
  row-gap: 20px;
`;

const LeaveInfoText = styled.p`
  margin: 0;
  font-size: 12px;
  color: var(--gray);
`;

const IconTextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const YearInfoText = styled.p`
  margin: 0;
  font-weight: bold;
`;

const IconBackground = styled.div<{ color?: string }>`
  background-color: ${({ color }) => getTransparentBackground(color)};
  border-radius: 50%;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
`;

const getTransparentBackground = (color?: string) => {
  if (!color) return 'transparent';
  if (color.startsWith('#')) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.2)`;
  }
  return color;
};
